import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import type {
  ClarificationAnswer,
  ExistingRuleRead,
  FilteringRuleConflictDetail,
  FilteringRuleRead,
  NeedsClarification,
} from '@/client'
import {
  adminConfirmFilteringRuleMutation,
  adminDeleteFilteringRuleMutation,
  adminGenerateFilteringRuleMutation,
  adminListFilteringRulesOptions,
  adminListFilteringRulesQueryKey,
  adminSetFilteringRuleEnabledMutation,
  parentRulesConfirmMutation,
  parentRulesDeleteMutation,
  parentRulesGenerateMutation,
  parentRulesListOptions,
  parentRulesListQueryKey,
  parentRulesSetEnabledMutation,
} from '@/client/@tanstack/react-query.gen'

export type FilteringRulesSource = 'admin' | 'parent'

const filteringIntentSchema = z.object({
  intent_text: z.string().trim().min(3, 'Describe one Filtering rule intent').max(500, 'Use 500 characters or fewer'),
})

type FilteringIntentValues = z.infer<typeof filteringIntentSchema>

const clarificationAnswerSchema = z.object({
  answer: z.string().trim().min(1, 'Enter an answer').max(500, 'Use 500 characters or fewer'),
})

type ClarificationAnswerValues = z.infer<typeof clarificationAnswerSchema>

function filteringRuleConflict(error: unknown): FilteringRuleConflictDetail | null {
  if (typeof error !== 'object' || error === null) {
    return null
  }
  const detail = Reflect.get(error, 'detail')
  if (typeof detail !== 'object' || detail === null) {
    return null
  }
  const code = Reflect.get(detail, 'code')
  const conflictingRuleId = Reflect.get(detail, 'conflicting_rule_id')
  if (code !== 'filtering-rule-conflict' || typeof conflictingRuleId !== 'string') {
    return null
  }
  return { code, conflicting_rule_id: conflictingRuleId }
}

const clientBySource = {
  admin: {
    listOptions: adminListFilteringRulesOptions,
    listQueryKey: adminListFilteringRulesQueryKey,
    generate: adminGenerateFilteringRuleMutation,
    confirm: adminConfirmFilteringRuleMutation,
    remove: adminDeleteFilteringRuleMutation,
    setEnabled: adminSetFilteringRuleEnabledMutation,
    refetchOnFocus: false,
  },
  parent: {
    listOptions: parentRulesListOptions,
    listQueryKey: parentRulesListQueryKey,
    generate: parentRulesGenerateMutation,
    confirm: parentRulesConfirmMutation,
    remove: parentRulesDeleteMutation,
    setEnabled: parentRulesSetEnabledMutation,
    refetchOnFocus: true,
  },
}

export function useFilteringRules({
  source,
  deviceId,
}: {
  source: FilteringRulesSource
  deviceId: string
}) {
  const queryClient = useQueryClient()
  const client = clientBySource[source]
  const [existingRule, setExistingRule] = useState<ExistingRuleRead | null>(null)
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [replacementTarget, setReplacementTarget] = useState<FilteringRuleRead | null>(null)
  const form = useForm<FilteringIntentValues>({
    resolver: zodResolver(filteringIntentSchema),
    defaultValues: { intent_text: '' },
  })
  const clarificationForm = useForm<ClarificationAnswerValues>({
    resolver: zodResolver(clarificationAnswerSchema),
    defaultValues: { answer: '' },
  })
  const [originalIntent, setOriginalIntent] = useState<string | null>(null)
  const [clarificationAnswers, setClarificationAnswers] = useState<ClarificationAnswer[]>([])
  const [generatedProposal, setGeneratedProposal] = useState<FilteringRuleRead | null>(null)
  const [clarification, setClarification] = useState<NeedsClarification | null>(null)
  const listOptions = { path: { device_id: deviceId } }
  const rules = useQuery({ ...client.listOptions(listOptions), refetchOnWindowFocus: client.refetchOnFocus })
  const listKey = client.listQueryKey(listOptions)
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: listKey })
  }
  const generate = useMutation({
    ...client.generate(),
    onSuccess: (result, variables) => {
      if (result.kind === 'needs_clarification') {
        setOriginalIntent(variables.body.intent_text)
        setClarificationAnswers(variables.body.clarification_answers ?? [])
        setClarification(result)
        clarificationForm.reset()
        return
      }
      setReplacementTarget(null)
      setOriginalIntent(null)
      setClarificationAnswers([])
      setClarification(null)
      clarificationForm.reset()
      form.reset()
      setGeneratedProposal(result.kind === 'rule_proposal' ? result.rule : null)
      setExistingRule(result.kind === 'existing_rule' ? result : null)
      refresh()
    },
  })
  const confirm = useMutation({
    ...client.confirm(),
    onSuccess: refresh,
  })
  const removeRule = useMutation({
    ...client.remove(),
    onSuccess: refresh,
  })
  const setEnabled = useMutation({
    ...client.setEnabled(),
    onError: (error) => {
      if (filteringRuleConflict(error) !== null) {
        setConflictDialogOpen(true)
      }
    },
    onSuccess: () => {
      setConflictDialogOpen(false)
      setExistingRule(null)
      refresh()
    },
  })
  const allRules = rules.data ?? []
  const proposedRules = allRules.filter((rule) => rule.status === 'proposed')
  const enabledRules = allRules.filter((rule) => rule.status === 'enabled')
  const disabledRules = allRules.filter((rule) => rule.status === 'disabled')
  const conflict = filteringRuleConflict(setEnabled.error)
  const conflictingRule = conflict
    ? allRules.find((rule) => rule.id === conflict.conflicting_rule_id)
    : undefined
  const requestedRule = conflict
    ? allRules.find((rule) => rule.id === setEnabled.variables?.path.rule_id)
    : undefined
  const conflictCanSwap =
    conflict !== null &&
    conflictingRule !== undefined &&
    requestedRule !== undefined &&
    conflictingRule.action !== requestedRule.action
  const mutationFailed =
    generate.isError ||
    confirm.isError ||
    removeRule.isError ||
    (setEnabled.isError && conflict === null)
  const mutationPending =
    generate.isPending || confirm.isPending || removeRule.isPending || setEnabled.isPending

  const submitIntent = (values: FilteringIntentValues): void => {
    setGeneratedProposal(null)
    setExistingRule(null)
    generate.mutate({
      path: { device_id: deviceId },
      body: {
        intent_text: values.intent_text,
        clarification_answers: [],
        replaces_rule_id: replacementTarget?.id ?? null,
      },
    })
  }

  const submitClarification = (answer: string): void => {
    if (originalIntent === null || clarification === null) {
      return
    }
    const nextAnswers = [
      ...clarificationAnswers,
      { question: clarification.question, answer },
    ]
    setClarificationAnswers(nextAnswers)
    setClarification(null)
    setGeneratedProposal(null)
    generate.mutate({
      path: { device_id: deviceId },
      body: {
        intent_text: originalIntent,
        clarification_answers: nextAnswers,
        replaces_rule_id: replacementTarget?.id ?? null,
      },
    })
  }

  const restartClarification = (): void => {
    form.reset({ intent_text: originalIntent ?? '' })
    clarificationForm.reset()
    setOriginalIntent(null)
    setClarificationAnswers([])
    setClarification(null)
    setGeneratedProposal(null)
    generate.reset()
  }

  const startReplacement = (rule: FilteringRuleRead): void => {
    form.reset({ intent_text: '' })
    clarificationForm.reset()
    setGeneratedProposal(null)
    setOriginalIntent(null)
    setClarificationAnswers([])
    setClarification(null)
    setExistingRule(null)
    setReplacementTarget(rule)
    generate.reset()
  }

  const cancelReplacement = (): void => {
    form.reset({ intent_text: '' })
    setReplacementTarget(null)
  }

  const confirmRule = (ruleId: string, onConfirmed?: () => void): void => {
    confirm.mutate({ path: { device_id: deviceId, rule_id: ruleId } }, { onSuccess: onConfirmed })
  }

  const deleteRule = (ruleId: string, onDeleted?: () => void): void => {
    removeRule.mutate({ path: { device_id: deviceId, rule_id: ruleId } }, { onSuccess: onDeleted })
  }

  const setRuleEnabled = (ruleId: string, enabled: boolean, conflictingRuleId?: string): void => {
    setEnabled.mutate({
      path: { device_id: deviceId, rule_id: ruleId },
      body:
        conflictingRuleId === undefined
          ? { enabled }
          : { enabled, conflicting_rule_id: conflictingRuleId },
    })
  }

  return {
    rules: allRules,
    rulesError: rules.isError,
    proposedRules,
    enabledRules,
    disabledRules,
    generatePending: generate.isPending,
    form,
    clarificationForm,
    originalIntent,
    existingRule,
    resetAuthoring: () => {
      setExistingRule(null)
      setGeneratedProposal(null)
    },
    generatedProposal,
    clarification,
    clarificationAnswers,
    submitIntent,
    submitClarification,
    restartClarification,
    replacementTarget,
    startReplacement,
    cancelReplacement,
    conflict,
    conflictingRule,
    requestedRule,
    conflictCanSwap,
    conflictDialogOpen,
    setConflictDialogOpen,
    confirmRule,
    deleteRule,
    setRuleEnabled,
    setEnabledPending: setEnabled.isPending,
    mutationPending,
    mutationFailed,
  }
}
